import { Box, Grid, Paper, Stack, Divider } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useParams } from 'react-router-dom';

import { ReactComponent as DocumentAlert } from 'common/assets/images/document-alert.svg';
import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import { ReactComponent as GreenTick } from 'common/assets/images/green-tick.svg';
import AccordionComp from 'common/components/Accordion/AccordionComp';
import { Loader } from 'common/components/Loader/Loader';
import { MODULES } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { APP_GENERIC_DATA_NOT_FOUND_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import ActionPlanLayout from 'features/HorizonScanning/components/ActionPlanLayout/ActionPlanLayout';

import {
	getMappingDetailsData,
	getRegInventoryHorizonStatus,
	getSummaryAssessmentData,
} from '../api';
import {
	IBusinessUnitMappingEntity,
	ICitationDetailsElements,
	IGroupCitationDetails,
} from '../api/type-def';
import CitationDescription from '../Components/CitationDescription/CitationDescription';
import RegulationBreadcrumbs, {
	ICustomBreadcrumbsProps,
} from '../Components/RegulationBreadcrumb/RegulationBreadcrumbs';
import RegulationDetailsHeader from '../Components/RegulationHeader/RegulationDetailsHeader';
import SubCitationSummary from '../RegulationInventoryDetails/SubCitationSummary/SubCitationSummary';

import SummaryDisplay from './SummaryDisplay';

import styles from './RegulationHorizon.module.scss';

const { OK, CREATED, BAD_REQUEST, NOT_FOUND, INTERNAL_SERVER_ERROR } =
	HTTP_STATUS_CODES;

const { REQUIREMENT_INVENTORY } = MODULES;

interface IRegulationHorizonProps {
	storageWrapper: any;
}
const RegulationHorizon = (props: IRegulationHorizonProps) => {
	const { storageWrapper } = props;
	const { groupId, regulationId } = useParams();
	const [loading, setIsLoading] = useState<boolean>(false);
	const [notification, setNotification] = useState<boolean>(true);
	const [releationshipStatus, setReleationshipStatus] =
		useState<boolean>(false);

	const [mappingAPIResponseData, setMappingAPIResponseData] = useState<
		IBusinessUnitMappingEntity[]
	>([]);
	const [summaryAssessment, setSummaryAssessment] =
		useState<IGroupCitationDetails>({} as IGroupCitationDetails);
	const [selectedAccordians, setSelectedAccordians] = useState<
		ICitationDetailsElements[]
	>([]);

	const sideDrawerState = useSelector(
		(state: RootState) => state.common.mappingDrawer,
	);

	const dispatch = useDispatch();

	const getSummaryAssessment = async (id: string): Promise<void> => {
		setIsLoading(true);
		const resp: any = await getSummaryAssessmentData(id);

		if (resp?.statusCode === OK || resp?.statusCode === CREATED) {
			setSummaryAssessment(resp.responseBody);
		} else if (
			resp?.statusCode === BAD_REQUEST ||
			resp.statusCode === NOT_FOUND
		) {
			const { errorBody } = resp;
			const responseError: any = resp.error;
			const errorMsg: string =
				(errorBody?.errorMessage as string) ||
				(responseError?.message as string) ||
				'Unknown error';
			dispatch(
				setToastMessage({
					message: errorMsg,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		} else if (resp?.statusCode === INTERNAL_SERVER_ERROR) {
			dispatch(
				setToastMessage({
					message: 'Internal Server Error',
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsLoading(false);
	};

	const getMappingDetails = async (groupId: string) => {
		setIsLoading(true);
		const resp = await getMappingDetailsData(groupId);
		setIsLoading(false);
		if (resp?.statusCode === OK && resp.responseBody) {
			setMappingAPIResponseData(resp.responseBody);
		}
	};

	const handleAccordianChange = (
		val: ICitationDetailsElements,
		event: React.SyntheticEvent,
	) => {
		event.stopPropagation();
		if (selectedAccordians.includes(val)) {
			setSelectedAccordians((res) =>
				res.filter((accordian) => accordian !== val),
			);
		} else {
			setSelectedAccordians([...selectedAccordians, val]);
		}
	};

	const getRelationshipCardStatus = async (
		groupId: string,
		regulationId: string,
	) => {
		setIsLoading(true);
		const response: any = await getRegInventoryHorizonStatus(
			groupId || '',
			regulationId || '',
		);
		setIsLoading(false);
		if (response?.statusCode === OK && response.responseBody) {
			const status: string = response.responseBody;
			const successStatus = status.toLowerCase() === 'reviewed';
			setReleationshipStatus(successStatus);
			setNotification(!successStatus);
		}
	};

	const drawerCloseHandler = () => {
		setNotification(false);
	};

	const drawerCloseSuccessHandler = () => {
		setReleationshipStatus(false);
	};

	useEffect(() => {
		if (groupId && regulationId) {
			getMappingDetails(groupId);
			getSummaryAssessment(groupId);
			getRelationshipCardStatus(groupId, regulationId);
		}
	}, [groupId, regulationId]);

	const handleAccordionText = (rvalue: any, event: any) => {
		handleAccordianChange(rvalue, event);
	};

	useEffect(() => {
		if (groupId && regulationId) {
			getRelationshipCardStatus(groupId, regulationId);
		}
	}, [sideDrawerState]);

	const breadCrumbs: ICustomBreadcrumbsProps[] = [
		{
			title: toSentenceCase(REQUIREMENT_INVENTORY),
			type: 'link',
			key: 'req-inv',
			path: rrcpDashboardRoutes.regulationInventory,
			dataTestId: 'inventory-breadcrumbs-link',
		},
		{
			title: summaryAssessment?.groupName || '',
			type: 'text',
			key: 'static-text',
		},
	];
	return (
		<Grid container spacing={2}>
			{loading && <Loader show className={styles['progress-bar']} />}
			{summaryAssessment.groupName ? (
				<>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<RegulationBreadcrumbs
							items={breadCrumbs}
							storageWrapper={storageWrapper}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						{summaryAssessment?.groupId && (
							<RegulationDetailsHeader
								taskId={summaryAssessment.taskId}
								status={summaryAssessment.taskStatus}
								assignedUserName={summaryAssessment.assignedUserName}
								jurisdiction={summaryAssessment.jurisdiction}
								reviewByDate={summaryAssessment.reviewByDate}
								title={summaryAssessment.groupName}
								mappingData={mappingAPIResponseData || []}
							/>
						)}
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						{notification && !releationshipStatus && (
							<Paper
								className={styles['notification-wrapper']}
								data-testid="notification-wrapper"
							>
								<Box className={styles['notification-message-wrapper']}>
									<DocumentAlert className={styles['notification-warning']} />
									<Stack direction="column">
										<Box className={styles['notification-error-message']}>
											One or multiple items have been impacted
										</Box>
										<Box>Please review and make the necessary changes</Box>
									</Stack>
									<DrawerClose
										onClick={drawerCloseHandler}
										className={styles['notification-close']}
										data-testid="close-notification-btn"
									/>
								</Box>
							</Paper>
						)}
						{releationshipStatus && !notification && (
							<Paper
								className={styles['notification-wrapper']}
								data-testid="notification-success"
							>
								<Box className={styles['notification-message-wrapper']}>
									<GreenTick className={styles['notification-success']} />
									<Stack direction="column">
										<Box className={styles['notification-success-message']}>
											All impacted items have been addressed
										</Box>
										<Box>Please review Tasks under Action Plan to confirm</Box>
									</Stack>
									<DrawerClose
										onClick={drawerCloseSuccessHandler}
										className={styles['notification-close']}
										data-testid="close-notification-success"
									/>
								</Box>
							</Paper>
						)}
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<SummaryDisplay
							changeAISummary={summaryAssessment.changeAiSummary || ''}
							relevantObligation={summaryAssessment.summary || ''}
							regId={regulationId || ''}
							groupId={groupId}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<ActionPlanLayout
							id={groupId}
							regId={regulationId || ''}
							moduleType={REQUIREMENT_INVENTORY}
							data={summaryAssessment}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<Divider />
					</Grid>
					<Grid
						item
						xs={12}
						sm={12}
						md={12}
						lg={12}
						xl={12}
						className={styles['section-header']}
					>
						Regulations
					</Grid>
					<Grid
						item
						xs={12}
						sm={12}
						md={12}
						lg={12}
						xl={12}
						className={styles['section-header-details']}
					>
						{summaryAssessment?.grouping?.map((res) => (
							<AccordionComp
								expanded={selectedAccordians.includes(res)}
								onChange={(event) => handleAccordianChange(res, event)}
								isLeftAccordian
								key={res.id}
								dataTestId="accordian-comp"
								summary={
									<SubCitationSummary
										checkedCitations={[]}
										selectedAccordians={selectedAccordians}
										res={res}
										isCheckBoxRequired={false}
										enableApplicablityStatus={false}
										isTaskidPresent={summaryAssessment.taskId !== null}
										handleAccordionText={handleAccordionText}
									/>
								}
								details={
									<CitationDescription
										currentNode={res}
										parentNode={res}
										isMultiCheckBoxRequired={false}
									/>
								}
							/>
						))}
					</Grid>
				</>
			) : (
				<>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<RegulationBreadcrumbs
							items={[breadCrumbs[0]]}
							storageWrapper={storageWrapper}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						&nbsp;
					</Grid>
					<Grid
						item
						xs={12}
						sm={12}
						md={12}
						lg={12}
						xl={12}
						data-testid="no-data-found"
					>
						{APP_GENERIC_DATA_NOT_FOUND_MSG}
					</Grid>
				</>
			)}
		</Grid>
	);
};
export default RegulationHorizon;
